import type { MutableRefObject } from 'react';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
  type TerraUIMessageDataPart,
  type ToolCallGenericMessage,
  parseNeoBackgroundInterruptField,
} from '@terra/shared-dto-chat-types';
import { terraProxyApiClient } from '../clients/testerClient';
import { useBackgroundExecutionStore } from '../store/backgroundExecutionStore';
import { useChatStreamResumeStore } from '../store/chat-stream-resume-store';
import { useExternalToolActivityStore } from '../store/externalToolActivityStore';
import { finalizeStreamingChatPart } from '../utils/finalizeStreamingChatParts';
import { abortChatForegroundWork } from '../app/providers/abort-chat-foreground-work';

export async function abortChatInstanceTurn({
  chatId,
  targetId,
  pauseQueueDrain,
  intentionalAbortRef,
  pauseQueueDrainAfterAbortRef,
  messagesRef,
  cancelScheduledRecovery,
  stop,
  setMessages,
  suppressNeoResume,
  suppressComposerLoading,
  clearPendingBackgroundExecutionsForAgentThread,
}: {
  chatId: string;
  targetId: string | undefined;
  pauseQueueDrain: boolean;
  intentionalAbortRef: MutableRefObject<boolean>;
  pauseQueueDrainAfterAbortRef: MutableRefObject<boolean>;
  messagesRef: MutableRefObject<TerraUIMessage[]>;
  cancelScheduledRecovery: () => void;
  stop: () => Promise<void> | void;
  setMessages: (messages: TerraUIMessage[]) => void;
  suppressNeoResume: (chatId: string) => void;
  suppressComposerLoading: (chatId: string) => void;
  clearPendingBackgroundExecutionsForAgentThread: (params: {
    agentThreadId: string;
  }) => void;
}): Promise<void> {
  intentionalAbortRef.current = true;
  useChatStreamResumeStore.getState().setStreamResuming(chatId, false);
  suppressNeoResume(chatId);
  suppressComposerLoading(chatId);
  useExternalToolActivityStore
    .getState()
    .setChatActivity({ chatId, active: false });
  pauseQueueDrainAfterAbortRef.current = pauseQueueDrain;
  cancelScheduledRecovery();

  await abortChatForegroundWork({ chatId });

  try {
    await stop();
  } catch (error: unknown) {
    console.warn(
      '[handleAbort] AI SDK stop failed after intentional abort; continuing local cleanup',
      error instanceof Error ? error : new Error(String(error)),
    );
  }

  const updatedMessages = messagesRef.current.map((message) => {
    if (!message.parts) {
      return message;
    }

    return {
      ...message,
      parts: message.parts.map((part) => finalizeStreamingChatPart(part)),
    };
  });

  setMessages(updatedMessages);

  if (chatId && targetId) {
    try {
      await terraProxyApiClient.abortTrace({
        threadId: chatId,
        applicationId: targetId,
      });
    } catch (error) {
      console.error('Failed to send abort signal to server:', error);
    }
  }

  if (!chatId) {
    return;
  }

  const pendingExecutionsForChat = [
    ...useBackgroundExecutionStore
      .getState()
      .pendingExecutionsByToolCallId.values(),
  ].filter(
    (execution) =>
      execution.copilotThreadId === chatId && execution.kind !== 'subagent',
  );
  const applicationIdByNeoThreadId = new Map(
    pendingExecutionsForChat.map((execution) => [
      execution.agentThreadId,
      execution.applicationId,
    ]),
  );

  const neoThreadIdsFromMessages = messagesRef.current.flatMap((message) =>
    (message.parts ?? []).flatMap((part) => {
      if (part.type !== TerraUIMessageDataPartName) {
        return [];
      }

      const { data } = part as TerraUIMessageDataPart;

      if (!data || data.type !== DataMessageTypeEnum.ToolCallUpdate) {
        return [];
      }

      const toolCall = data as ToolCallGenericMessage;

      if (
        toolCall.toolName !== 'attacker-tool' ||
        toolCall.status !== 'in-progress'
      ) {
        return [];
      }

      const neoPayload = parseNeoBackgroundInterruptField(toolCall.output);

      if (!neoPayload?.neoThreadId) {
        return [];
      }

      return [neoPayload.neoThreadId];
    }),
  );
  const neoThreadIdsFromPending = pendingExecutionsForChat.map(
    (execution) => execution.agentThreadId,
  );
  const neoThreadIds = [
    ...new Set([...neoThreadIdsFromMessages, ...neoThreadIdsFromPending]),
  ];

  if (neoThreadIds.length === 0) {
    return;
  }

  await Promise.all(
    neoThreadIds.map((neoThreadId) => {
      const neoApplicationId =
        applicationIdByNeoThreadId.get(neoThreadId) ?? targetId;

      if (!neoApplicationId) {
        console.warn(
          `[handleAbort] Missing applicationId for Neo execution ${neoThreadId}, skipping server abort`,
        );

        return Promise.resolve();
      }

      return terraProxyApiClient
        .abortNeoExecution({
          threadId: neoThreadId,
          applicationId: neoApplicationId,
        })
        .catch((error: unknown) => {
          console.warn(
            `[handleAbort] Failed to abort Neo execution ${neoThreadId}:`,
            error,
          );
        });
    }),
  );

  for (const agentThreadId of neoThreadIds) {
    clearPendingBackgroundExecutionsForAgentThread({ agentThreadId });
  }
}
